/**
 * upgradeOptions.ts — pure logic for the "Plans" mode of the shared Upgrade
 * dialog (web/src/components/entitlements/UpgradeDialogProvider.tsx). No
 * React, no network — every function here takes plain data and returns plain
 * data so it can be unit-tested with `bun test`
 * (see upgradeOptions.test.ts, same style as permissionFeatureMap.test.ts).
 *
 * Owner request 2026-09-21 ("so for the upgrade panel only show current and
 * upgrade options. also need to include the options and individual seat
 * addition with qty field"):
 *   1. Plan section — current plan highlighted, then only plans that are a
 *      genuine UPGRADE from it (see getUpgradePlanRows() for the exact rule).
 *   2. Add-ons section — every catalog add-on, "Active" when already granted.
 *   3. Extra users section — only when the current plan allows it; a qty
 *      field (1-50) times the plan's extraSeatPrice.
 */
import type { Entitlements, FeatureKey } from '../../../shared/entitlements'
import { hasFeature } from '../../../shared/entitlements'
import type { CatalogAddon, CatalogPlan } from '../hooks/useEntitlementsCatalog'

export const MIN_EXTRA_SEATS = 1
export const MAX_EXTRA_SEATS = 50

export type CurrentPlanInfo = Pick<Entitlements, 'planKind' | 'tierRank'> | null | undefined

export type UpgradePlanRow = {
  plan: CatalogPlan

  /** True when this row is a TEAM plan being offered to a SOLO account — label it "Team - N users". */
  isTeamUpgradeForSolo: boolean
}

/**
 * Which catalog plans count as an upgrade from the tenant's current plan.
 *
 * Rules (owner spec, 2026-09-21):
 *  - never the 'internal' plan kind, never a plan with no tierRank
 *  - never the current plan itself, never a LOWER plan
 *  - TEAM account: only higher-rank TEAM plans (solo is never offered to a team)
 *  - SOLO account: higher-rank SOLO plans, PLUS same-or-higher-rank TEAM
 *    plans (moving from single-user to a team is itself an upgrade)
 *  - unknown current plan (no tierRank, or an 'internal' plan) -> []
 */
export function getUpgradePlanRows(plans: CatalogPlan[], current: CurrentPlanInfo): UpgradePlanRow[] {
  if (!current || current.tierRank == null || current.planKind === 'internal') return []

  const currentRank = current.tierRank
  const real = plans.filter(p => p.planKind !== 'internal' && p.tierRank != null)

  if (current.planKind === 'team') {
    return real
      .filter(p => p.planKind === 'team' && (p.tierRank as number) > currentRank)
      .sort((a, b) => (a.tierRank as number) - (b.tierRank as number))
      .map(plan => ({ plan, isTeamUpgradeForSolo: false }))
  }

  // solo
  const soloUpgrades = real
    .filter(p => p.planKind === 'solo' && (p.tierRank as number) > currentRank)
    .sort((a, b) => (a.tierRank as number) - (b.tierRank as number))
    .map(plan => ({ plan, isTeamUpgradeForSolo: false }))

  const teamUpgrades = real
    .filter(p => p.planKind === 'team' && (p.tierRank as number) >= currentRank)
    .sort((a, b) => (a.tierRank as number) - (b.tierRank as number))
    .map(plan => ({ plan, isTeamUpgradeForSolo: true }))

  return [...soloUpgrades, ...teamUpgrades]
}

/** Label for a plan row — "Team - N users" for a solo->team upgrade row, else the plan's own name. */
export function planRowLabel(row: UpgradePlanRow): string {
  if (row.isTeamUpgradeForSolo) return `Team - ${row.plan.maxUsers ?? '?'} users`

  return row.plan.name
}

/** Is this catalog add-on already switched on for the account? */
export function isAddonActive(
  entitlements: Entitlements | null | undefined,
  addon: Pick<CatalogAddon, 'grantsFeatureKey'>
): boolean {
  return hasFeature(entitlements, addon.grantsFeatureKey as FeatureKey)
}

export type SeatQtyValidation = { qty: number | null; error: string | null }

/** Validates the extra-seats quantity field: a whole number in [MIN_EXTRA_SEATS, MAX_EXTRA_SEATS]. */
export function validateSeatQty(raw: string | number): SeatQtyValidation {
  if (raw === '' || raw === null || raw === undefined) {
    return { qty: null, error: 'Enter a whole number.' }
  }

  const n = typeof raw === 'number' ? raw : Number(raw)

  if (!Number.isFinite(n) || !Number.isInteger(n)) {
    return { qty: null, error: 'Enter a whole number.' }
  }
  if (n < MIN_EXTRA_SEATS) return { qty: null, error: `Minimum ${MIN_EXTRA_SEATS} seat.` }
  if (n > MAX_EXTRA_SEATS) return { qty: null, error: `Maximum ${MAX_EXTRA_SEATS} seats.` }

  return { qty: n, error: null }
}

/** qty * price-per-seat — null when either input is missing (no valid qty, or no price on this plan). */
export function computeExtraSeatsTotal(qty: number | null | undefined, pricePerSeat: number | null | undefined): number | null {
  if (qty == null || pricePerSeat == null) return null

  return qty * pricePerSeat
}

/** Whole-dollar prices render with no cents; anything else keeps 2 decimals. */
export function moneyFractionDigitsFor(amount: number | null | undefined): { minimumFractionDigits: number; maximumFractionDigits: number } {
  const whole = amount == null || Number.isInteger(amount)

  return { minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: whole ? 0 : 2 }
}
